import { addMinutes, format, getDay, isDate, startOfDay, subDays, subMinutes } from 'date-fns';
import { toZonedTime, format as tzFormat } from 'date-fns-tz';

type OptionalDate = Date | undefined | null;

export { fromZonedTime, toZonedTime, format as tzFormat } from 'date-fns-tz';

export const dateFormat = 'MM/dd/yyyy';
export const dateLongFormat = 'MMMM do yyyy';
export const timeFormat = 'h:mm a XXX';
export const timeLongFormat = 'hh:mm:ss a XXX';

// Dates without times are stored with time UTC 00:00, which means the date we get after converting to local time might be the wrong day.
// Adding the UTC offset makes the time 00:00 in the current timezone, which will result in the correct day.
// UTC correction should be used only if we know the date came from our DatePicker in date mode
export const formatDate = (date: OptionalDate, utcCorrection = false): string => {
  return date
    ? format(utcCorrection ? addMinutes(date, date.getTimezoneOffset()) : date, dateFormat)
    : '';
};

export const formatDateLong = (date: OptionalDate, utcCorrection = false): string =>
  date
    ? format(utcCorrection ? addMinutes(date, date.getTimezoneOffset()) : date, 'MMMM dd yyyy')
    : '';
export const formatDateWithComma = (date: OptionalDate, utcCorrection = false): string =>
  date
    ? format(utcCorrection ? addMinutes(date, date.getTimezoneOffset()) : date, 'MMMM dd, yyyy')
    : '';
export const formatTime = (date: OptionalDate): string => (date ? format(date, timeFormat) : '');
export const formatTimeLong = (date: OptionalDate): string =>
  date ? format(date, 'h:mm:ss a XXX') : '';

// When formatting date and time, obviously we don't want to add the UTC offset, because we show the timezone in the output
export const formatDateAndTime = (date: OptionalDate): string =>
  date ? format(date, dateFormat + ' ' + timeFormat) : '';
export const formatDateAndTimeLong = (date: OptionalDate): string =>
  date ? format(date, dateLongFormat + ' ' + timeLongFormat) : '';

export const getThreeLetterTimeZone = (date: Date, timeZone: string): string => {
  const zonedDate = toZonedTime(date, timeZone);
  const tz = tzFormat(zonedDate, 'zzz', { timeZone });
  return tz;
};

export const getTimeStringInTimeZone = (date: Date, timeZone: string): string => {
  const zonedDate = toZonedTime(date, timeZone);
  const tz = tzFormat(zonedDate, 'h:mm aa zzz', { timeZone });
  return tz;
};

export const getTimeZone = (timeZone: string): string | undefined => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timeZone,
    timeZoneName: 'shortOffset'
  });
  const parts = formatter.formatToParts(new Date());
  const tzPart = parts.find(p => p.type === 'timeZoneName');
  return tzPart?.value;
};

export const getToday = (): Date =>
  addMinutes(startOfDay(new Date()), -new Date().getTimezoneOffset());

export function utcToLocal(utc: Date): string {
  const localOffsetInMinutes: number = new Date().getTimezoneOffset();
  const correctedForTimezone: Date = subMinutes(utc, localOffsetInMinutes);
  const displayableDate: string = format(correctedForTimezone, 'MM/dd/yyyy HH:mm:ss O');
  return displayableDate;
}
export function localDateFormat(localDate: Date): string {
  const displayableDate: string = format(localDate, 'MM/dd/yyyy HH:mm:ss O');
  return displayableDate;
}
export function localDateOnlyFormat(localDate: Date): string {
  const displayableDate: string = format(localDate, 'MM/dd/yyyy');
  return displayableDate;
}

export function dateToStringRecursive(obj: unknown): any {
  if (obj && typeof obj === 'object') {
    if (isDate(obj) && obj instanceof Date) {
      return localDateOnlyFormat(obj);
    } else if (Array.isArray(obj)) {
      return obj.map(item => dateToStringRecursive(item));
    } else {
      Object.keys(obj).forEach(key => {
        obj[key] = dateToStringRecursive(obj[key]);
      });
    }
  }

  return obj;
}
export function stringToDateRecursive(obj: unknown): any {
  if (obj && typeof obj === 'object') {
    if (Array.isArray(obj)) {
      return obj.map(item => stringToDateRecursive(item));
    } else {
      Object.keys(obj).forEach(key => {
        obj[key] = stringToDateRecursive(obj[key]);
      });
    }
  } else if (typeof obj === 'string') {
    const timestamp = Date.parse(obj);
    if (isNaN(timestamp)) {
      //string is not date return normal value
      return obj;
    } else {
      //string was a date return date
      return new Date(obj);
    }
  }

  return obj;
}

export const getDayLightOffSetInMilliseconds = (d: Date): number => {
  return d.getTimezoneOffset() * 60 * 100;
};

export const getOffSetTimeInMilliseconds = (d: Date, timezoneOffsetInHours: number): number => {
  const localTime = d.getTime();
  const localOffset = getDayLightOffSetInMilliseconds(d);
  const utcTime = localTime + localOffset;
  const offsetDayLight =
    dstOffsetAtDate(d) !== 0 ? timezoneOffsetInHours + 1 : timezoneOffsetInHours;
  return utcTime + 60 * 60 * 1000 * offsetDayLight;
};

export const getCurrentTimeInTimeZone = (timezoneOffsetInHours: number): Date => {
  const d = new Date();
  const utc = d.getTime() + d.getTimezoneOffset() * 60000;
  return new Date(utc + 3600000 * timezoneOffsetInHours);
};

export const getTimeInTimeZone = (d: Date, timezoneOffsetInHours: number): Date => {
  const utc = d.getTime() + d.getTimezoneOffset() * 60000;
  return new Date(utc + 3600000 * timezoneOffsetInHours);
};

//https://stackoverflow.com/questions/11887934/how-to-check-if-dst-daylight-saving-time-is-in-effect-and-if-so-the-offset
/// Function to return if the date currently observing Daylight Savings Time
function dstOffsetAtDate(dateInput) {
  const fullYear = dateInput.getFullYear() | 0;
  // "Leap Years are any year that can be exactly divided by 4 (2012, 2016, etc)
  //   except if it can be exactly divided by 100, then it isn't (2100,2200,etc)
  //    except if it can be exactly divided by 400, then it is (2000, 2400)"
  // (https://www.mathsisfun.com/leap-years.html).
  const isLeapYear = ((fullYear & 3) | ((fullYear / 100) & 3)) === 0 ? 1 : 0;
  // (fullYear & 3) = (fullYear % 4), but faster
  //Alternative:var isLeapYear=(new Date(currentYear,1,29,12)).getDate()===29?1:0
  const fullMonth = dateInput.getMonth() | 0;
  return (
    // 1. We know what the time since the Epoch really is
    +dateInput - // same as the dateInput.getTime() method
    // 2. We know what the time since the Epoch at the start of the year is
    +new Date(fullYear, 0) - // day defaults to 1 if not explicitly zeroed
    // 3. Now, subtract what we would expect the time to be if daylight savings
    //      did not exist. This yields the time-offset due to daylight savings.
    // Calculate the day of the year in the Gregorian calendar
    // The code below works based upon the facts of signed right shifts
    //    • (x) >> n: shifts n and fills in the n highest bits with 0s
    //    • (-x) >> n: shifts n and fills in the n highest bits with 1s
    // (This assumes that x is a positive integer)
    ((((-1 + // first day in the year is day 1
      (31 & (-fullMonth >> 4)) + // January // (-11)>>4 = -1
      ((28 + isLeapYear) & ((1 - fullMonth) >> 4)) + // February
      (31 & ((2 - fullMonth) >> 4)) + // March
      (30 & ((3 - fullMonth) >> 4)) + // April
      (31 & ((4 - fullMonth) >> 4)) + // May
      (30 & ((5 - fullMonth) >> 4)) + // June
      (31 & ((6 - fullMonth) >> 4)) + // July
      (31 & ((7 - fullMonth) >> 4)) + // August
      (30 & ((8 - fullMonth) >> 4)) + // September
      (31 & ((9 - fullMonth) >> 4)) + // October
      (30 & ((10 - fullMonth) >> 4)) + // November
      // There are no months past December: the year rolls into the next.
      // Thus, fullMonth is 0-based, so it will never be 12 in Javascript

      (dateInput.getDate() | 0)) & // get day of the month
      0xffff) *
      24 *
      60 + // 24 hours in a day, 60 minutes in an hour
      (dateInput.getHours() & 0xff) * 60 + // 60 minutes in an hour
      (dateInput.getMinutes() & 0xff)) |
      0) *
      60 *
      1000 - // 60 seconds in a minute * 1000 milliseconds in a second
    (dateInput.getSeconds() & 0xff) * 1000 - // 1000 milliseconds in a second
    dateInput.getMilliseconds()
  );
}

// Convert a string in DD:HH:MM:SS or HH:MM:SS or MM:SS format to minutes. Used for timer components
export function timeLengthToMinutes(hhmmss: string): number {
  if (hhmmss.includes(':')) {
    const separatedByMeasurement = hhmmss.split(':');

    switch (separatedByMeasurement.length) {
      case 2:
        return parseInt(separatedByMeasurement[0], 10);
      case 3: {
        const hours = parseInt(separatedByMeasurement[0], 10);
        const minutes = parseInt(separatedByMeasurement[1], 10);
        const totalMins: number = hours * 60 + minutes;
        return totalMins;
      }
      case 4: {
        const days = parseInt(separatedByMeasurement[0], 10);
        const hours = parseInt(separatedByMeasurement[1], 10);
        const minutes = parseInt(separatedByMeasurement[2], 10);
        const totalMins: number = days * 24 * 60 + hours * 60 + minutes;
        return totalMins;
      }
    }
  }
  return 0;
}

export const formatDateTimeChart = (value: string | number, excludeMinutes: boolean): string => {
  if (isNaN(Number(value))) {
    return `0 hrs`;
  }
  const h = Math.floor(Number(value) / 60);
  const m = Number(value) % 60;
  // Less than an hour
  if (h < 1) {
    // if zero, show it
    if (m == 0) {
      return '0 min';
    }

    // show only minutes, if less than 10 minutes, then we show the decimal
    return `${m < 10 ? m.toFixed(2) : m.toFixed()} min`;
  }
  if (excludeMinutes) {
    return `${h < 10 ? h : h} hrs`;
  }
  return `${h < 10 ? h : h}hrs ${m < 10 ? m.toFixed() : m.toFixed()} min`;
};

// turn a date stored as yyyy-mm-dd to a Date object
export function dateOnlyToDate(value: string | number | undefined | Date) {
  if (value) {
    switch (typeof value) {
      case 'string': {
        const stringifiedValue = value.toString();
        const datePartArray = stringifiedValue.split('-');

        if (datePartArray.length === 3) {
          const year = +datePartArray[0];
          const month = +datePartArray[1] - 1; //months are 0 index, so subtract one
          const day = +datePartArray[2];

          const correctDate = new Date(year, month, day);
          return correctDate;
        }
        break;
      }
      case 'object':
        //Unfortunately JavaScript will take 'yyyy-mm-dd' and
        //assume it's UTC while converting to a Date. When creating
        //the Date, JS automatically converts that to the user's
        //local time, which was never the intent of using DateOnly,
        //so we undo that here.
        //if the time is midnight, we've already done this and can skip the calculations.
        if (value.getHours() !== 0) {
          const offsetInMins = new Date().getTimezoneOffset();
          value.setMinutes(value.getMinutes() + offsetInMins);
        }
        return value;
      default:
        break;
    }
  }

  return null;
}

export function dateToDateOnly(value: Date | null) {
  if (value) {
    let dateOnlyFormat = '';
    const newDate = new Date(value);
    const year = newDate.getFullYear();

    let month: string | number = newDate.getMonth() + 1; //getMonth returns 0 for january, so add one
    if (month < 10) {
      month = `0${month}`;
    }

    let day: string | number = newDate.getDate();
    if (day < 10) {
      day = `0${day}`;
    }

    dateOnlyFormat = `${year}-${month}-${day}`;
    return dateOnlyFormat;
  }
  return null;
}

export function sameDates(firstDate: string | Date, secondDate: string | Date) {
  // Extract the date part from the meetingDate string
  const date = new Date(firstDate);
  const meetingDay = date.getUTCDate(); // 17
  const meetingMonth = date.getUTCMonth() + 1; // 4 (months are 0-indexed)
  const meetingYear = date.getUTCFullYear(); // 2025

  const dateToCheck = new Date(secondDate);
  const checkDay = dateToCheck.getUTCDate(); // 17
  const checkMonth = dateToCheck.getUTCMonth() + 1; // 4 (months are 0-indexed)
  const checkYear = dateToCheck.getUTCFullYear(); // 2025

  // Compare the formatted dates
  if (meetingDay == checkDay && meetingMonth == checkMonth && meetingYear == checkYear) {
    return true;
  }
}

export function getLastMonday(date = new Date()) {
  // Normalize to start of the day (local midnight)
  const today = startOfDay(date);

  const day = getDay(today); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  const daysToSubtract = day === 1 ? 7 : (day + 6) % 7;

  return subDays(today, daysToSubtract);
}

export function getLastSunday(date = new Date()) {
  let dayOfWeek = date.getDay(); // 0 for Sunday, 1 for Monday, ..., 6 for Saturday

  if (dayOfWeek === 0) {
    // If today is Sunday, get the Sunday of the previous week
    dayOfWeek = 7;
  }

  // Calculate the date of the last Sunday
  // If date is Sunday (dayOfWeek = 0), subtract 7 days to get the Sunday of the previous week.
  // If date is Monday (dayOfWeek = 1), subtract 1 day to get yesterday's date (which was Sunday).
  const lastSunday = new Date(date.getFullYear(), date.getMonth(), date.getDate() - dayOfWeek);

  return lastSunday;
}
export type RangeKey = 'Last 7 Days' | '1M' | 'QTR' | '6M' | 'YTD' | '1Y';
export const DATE_RANGES_LABELS: Record<RangeKey, string> = {
  'Last 7 Days': 'Last 7 Days',
  '1M': 'Last Month',
  QTR: 'Last Quarter',
  '6M': 'Last Six Months',
  YTD: 'Year to Date',
  '1Y': 'Last Year'
};
export const DATE_RANGES: RangeKey[] = ['Last 7 Days', '1M', 'QTR', '6M', 'YTD', '1Y'];
export function getDateRange(
  range: RangeKey,
  options?: { minDate?: Date; now?: Date }
): [string, string] {
  const now = options?.now ? new Date(options.now) : new Date();
  let start = new Date(now);
  let end = new Date(now);
  const addDays = (d: Date, n: number) => {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    return x;
  };
  const addMonths = (d: Date, n: number) => {
    const x = new Date(d);
    x.setMonth(x.getMonth() + n);
    return x;
  };
  switch (range) {
    case 'Last 7 Days':
      start = addDays(now, -7);
      break;
    case '1M':
      start = new Date(
        Date.UTC(
          now.getUTCFullYear(),
          now.getUTCMonth(),
          now.getUTCDate() - 30,
          now.getUTCHours(),
          now.getUTCMinutes(),
          now.getUTCSeconds()
        )
      );
      break;
    case '6M':
      start = new Date(
        Date.UTC(
          now.getUTCFullYear(),
          now.getUTCMonth() - 6,
          now.getUTCDate(),
          now.getUTCHours(),
          now.getUTCMinutes(),
          now.getUTCSeconds()
        )
      );
      break;
    case 'YTD':
      start = new Date(now.getFullYear(), 0, 1);
      break;
    case '1Y':
      // last year from today
      start = new Date(
        Date.UTC(
          now.getUTCFullYear() - 1,
          now.getUTCMonth(),
          now.getUTCDate(),
          now.getUTCHours(),
          now.getUTCMinutes(),
          now.getUTCSeconds()
        )
      );
      break;
    case 'QTR':
      {
        // Determine the current quarter (0–3)
        const q = Math.floor(now.getMonth() / 3);
        const year = now.getFullYear();
        // The last quarter is q-1 (or Q4 of the previous year if q=0)
        let lastQuarterYear = year;
        let lastQuarterStartMonth = (q - 1) * 3;
        if (q === 0) {
          lastQuarterYear = year - 1;
          lastQuarterStartMonth = 9; // Oct of previous year
        }
        // Start at first day of that quarter
        start = new Date(lastQuarterYear, lastQuarterStartMonth, 1);
        // End is the last day of that quarter
        const nextQuarter = new Date(lastQuarterYear, lastQuarterStartMonth + 3, 0); // day 0 = last day of prev month
        end = new Date(
          nextQuarter.getFullYear(),
          nextQuarter.getMonth(),
          nextQuarter.getDate(),
          23,
          59,
          59,
          999
        );
      }
      break;
    default:
      start = addMonths(now, -6);
  }
  if (options?.minDate && start < options.minDate) {
    start = new Date(options.minDate);
  }
  return [format(start.toISOString(), 'yyyy-MM-dd'), format(end.toISOString(), 'yyyy-MM-dd')];
}